import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit } from 'lucide-react';
import { authedJson } from '../../utils/adminApi';
import { uploadImageToSiteMedia } from '../../utils/adminMedia';

export default function StoreEditor() {
  const [items, setItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [variantForm, setVariantForm] = useState({ product_id: '', name: '', sku: '', price: '', stock: '' });
  const [editingVariantIdx, setEditingVariantIdx] = useState(-1);

  useEffect(() => {
    const load = async () => {
      const stored = await authedJson('/api/admin/store-products').catch(() => []);
      setItems(Array.isArray(stored) ? stored : []);
      const vars = await authedJson('/api/admin/store-variants').catch(() => []);
      setVariants(Array.isArray(vars) ? vars : []);
    };
    load();
  }, []);

  const [itemForm, setItemForm] = useState({ id: '', name: '', price: '', tag: '', image: '' });
  const [editingIdx, setEditingIdx] = useState(-1);

  const addOrUpdateItem = async (e) => {
    e.preventDefault();
    setFeedback({ error: '', success: '' });
    try {
      if (editingIdx >= 0) {
        const current = items[editingIdx];
        const updatedItem = await authedJson('/api/admin/store-products', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: current.id,
            name: itemForm.name,
            price_cents: Math.round(Number(itemForm.price.replace(/[^\d]/g, '')) * 100) || 0,
            currency: 'GHS',
            tag: itemForm.tag,
            image_url: itemForm.image,
            is_active: true
          })
        });
        setItems(items.map((it, i) => (i === editingIdx ? updatedItem : it)));
        setEditingIdx(-1);
        setFeedback({ error: '', success: 'Store item updated.' });
      } else {
        const created = await authedJson('/api/admin/store-products', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: itemForm.name,
            price_cents: Math.round(Number(itemForm.price.replace(/[^\d]/g, '')) * 100) || 0,
            currency: 'GHS',
            tag: itemForm.tag,
            image_url: itemForm.image,
            is_active: true
          })
        });
        setItems([created, ...items]);
        setFeedback({ error: '', success: 'Store item added.' });
      }
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
    setItemForm({ id: '', name: '', price: '', tag: '', image: '' });
  };

  const editItem = (idx) => { 
    const item = items[idx];
    setEditingIdx(idx);
    const price = item.price_cents ? `GHS ${Math.round(item.price_cents / 100)}` : item.price;
    setItemForm({ id: item.id, name: item.name, price, tag: item.tag, image: item.image_url });
  };
  const deleteItem = async (idx) => {
    if (!window.confirm('Delete this store item?')) return;
    setFeedback({ error: '', success: '' });
    try {
      const id = items[idx].id;
      await authedJson('/api/admin/store-products', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      setItems(items.filter((_, i) => i !== idx));
      setFeedback({ error: '', success: 'Store item deleted.' });
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }} className="bg-[#0A0E11] rounded-xl p-8 border border-[#A1FF4F]/10">
      <h2 className="text-2xl font-bold mb-4">Store Items Editor</h2>
      {feedback.error && <p className="mb-3 text-red-300 text-sm">{feedback.error}</p>}
      {feedback.success && <p className="mb-3 text-[#A1FF4F] text-sm">{feedback.success}</p>}
      <form onSubmit={addOrUpdateItem} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <input value={itemForm.name} onChange={(e)=>setItemForm({...itemForm, name:e.target.value})} placeholder="name" className="p-3 bg-[#0F1419] rounded" />
        <input value={itemForm.price} onChange={(e)=>setItemForm({...itemForm, price:e.target.value})} placeholder="price" className="p-3 bg-[#0F1419] rounded" />
        <input value={itemForm.tag} onChange={(e)=>setItemForm({...itemForm, tag:e.target.value})} placeholder="tag (NEW/DROP)" className="p-3 bg-[#0F1419] rounded" />
        <input value={itemForm.image} onChange={(e)=>setItemForm({...itemForm, image:e.target.value})} placeholder="image url (optional)" className="p-3 bg-[#0F1419] rounded" />
        <input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const url = await uploadImageToSiteMedia(file, 'store-products');
              setItemForm({ ...itemForm, image: url });
            } catch (error) {
              setFeedback({ error: error.message, success: '' });
            }
          }}
          className="p-3 bg-[#0F1419] rounded text-white/70"
        />
        <div className="col-span-4 flex items-center gap-3">
          <button type="submit" className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded">
            {editingIdx>=0? 'Update':'Add'}
          </button>
          {editingIdx>=0 && <button type="button" onClick={()=>{setEditingIdx(-1); setItemForm({ id: '', name: '', price: '', tag: '', image: '' });}} className="px-3 py-2 bg-white/5 rounded">Cancel</button>}
        </div>
      </form>

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
            <div className="flex items-center gap-3">
              <img src={item.image_url || item.image} alt={item.name} className="w-16 h-10 object-cover rounded" />
              <div>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-white/60">GHS {Math.round((item.price_cents || 0) / 100)} • {item.tag}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={()=>editItem(idx)} className="p-2 bg-white/5 rounded"><Edit className="w-4 h-4"/></button>
              <button onClick={()=>deleteItem(idx)} className="p-2 bg-red-600/20 rounded"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-white/60 mt-4">No store items yet.</p>}

      <div className="mt-8 pt-6 border-t border-white/10">
        <h3 className="text-xl font-bold mb-3">Product Variants</h3>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setFeedback({ error: '', success: '' });
            const payload = {
              product_id: variantForm.product_id,
              name: variantForm.name,
              sku: variantForm.sku || null,
              price_cents: variantForm.price ? Math.round(Number(variantForm.price.replace(/[^\d]/g, '')) * 100) : null,
              stock: variantForm.stock ? Number(variantForm.stock) : null
            };
            if (!payload.product_id || !payload.name.trim()) {
              setFeedback({ error: 'Select a product and enter a variant name.', success: '' });
              return;
            }
            if (payload.stock != null && payload.stock < 0) {
              setFeedback({ error: 'Variant stock cannot be negative.', success: '' });
              return;
            }
            try {
              if (editingVariantIdx >= 0) {
                const current = variants[editingVariantIdx];
                const updated = await authedJson('/api/admin/store-variants', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: current.id, ...payload })
                });
                setVariants(variants.map((v, i) => (i === editingVariantIdx ? updated : v)));
                setFeedback({ error: '', success: 'Variant updated.' });
                setEditingVariantIdx(-1);
              } else {
                const created = await authedJson('/api/admin/store-variants', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload)
                });
                setVariants([created, ...variants]);
                setFeedback({ error: '', success: 'Variant added.' });
              }
            } catch (error) {
              setFeedback({ error: error.message, success: '' });
            }
            setVariantForm({ product_id: '', name: '', sku: '', price: '', stock: '' });
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4"
        >
          <select
            value={variantForm.product_id}
            onChange={(e) => setVariantForm({ ...variantForm, product_id: e.target.value })}
            className="p-3 bg-[#0F1419] rounded text-white/80"
          >
            <option value="">Select product</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          <input value={variantForm.name} onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })} placeholder="variant name (e.g. Small)" className="p-3 bg-[#0F1419] rounded" />
          <input value={variantForm.sku} onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })} placeholder="SKU (optional)" className="p-3 bg-[#0F1419] rounded" />
          <input value={variantForm.price} onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })} placeholder="price override (optional)" className="p-3 bg-[#0F1419] rounded" />
          <input value={variantForm.stock} onChange={(e) => setVariantForm({ ...variantForm, stock: e.target.value })} placeholder="stock (optional)" className="p-3 bg-[#0F1419] rounded" />
          <div className="flex items-center gap-3">
            <button type="submit" className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded">
              {editingVariantIdx>=0 ? 'Update' : 'Add'}
            </button>
            {editingVariantIdx>=0 && <button type="button" onClick={()=>{setEditingVariantIdx(-1); setVariantForm({ product_id: '', name: '', sku: '', price: '', stock: '' });}} className="px-3 py-2 bg-white/5 rounded">Cancel</button>}
          </div>
        </form>

        <div className="space-y-3">
          {variants.map((v, idx) => (
            <div key={v.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
              <div>
                <div className="font-semibold">{v.name}</div>
                <div className="text-sm text-white/60">Product: {items.find((i) => i.id === v.product_id)?.name || v.product_id}</div>
                <div className="text-sm text-white/60">Price: {v.price_cents ? `GHS ${Math.round(v.price_cents / 100)}` : 'uses base price'} • Stock: {v.stock ?? '—'}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>{setEditingVariantIdx(idx); setVariantForm({ product_id: v.product_id, name: v.name, sku: v.sku || '', price: v.price_cents ? `GHS ${Math.round(v.price_cents / 100)}` : '', stock: v.stock ?? '' });}} className="p-2 bg-white/5 rounded"><Edit className="w-4 h-4"/></button>
                <button onClick={async ()=>{ 
                  setFeedback({ error: '', success: '' });
                  try {
                    await authedJson('/api/admin/store-variants', {
                      method: 'DELETE',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: v.id })
                    });
                    setVariants(variants.filter((_, i) => i !== idx));
                    setFeedback({ error: '', success: 'Variant deleted.' });
                  } catch (error) {
                    setFeedback({ error: error.message, success: '' });
                  }
                }} className="p-2 bg-red-600/20 rounded"><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
